import React, { type JSX } from "react";
import { Heading, Text } from "../../../../components/common/typography";

export type NumberedItem = {
  text: string | JSX.Element;
  subItems?: (string | JSX.Element)[];
  isBold?: boolean;
  numberedSubItems?: boolean;
};

export type NumberingConfig = {
  main: number;
  continueFrom?: number;
};

type LegalNumberedListProps = {
  startNumber: number | NumberingConfig;
  items: NumberedItem[];
  isBold?: boolean;
  sectionTitle?: string;
  sectionSubtitle?: string | JSX.Element;
};

const getNextNumber = (config: number | NumberingConfig, index: number) => {
  if (typeof config === "number") {
    return `${config}.${index + 1}`;
  }
  return `${config.main}.${(config.continueFrom || 0) + index + 1}`;
};

const getSectionNumber = (config: number | NumberingConfig) => {
  return typeof config === "number" ? config : config.main;
};

export const LegalNumberedList: React.FC<LegalNumberedListProps> = ({
  startNumber,
  items,
  isBold,
  sectionTitle,
  sectionSubtitle,
}) => {
  return (
    <>
      {sectionTitle && (
        <div className="mt-8 mb-6">
          <Heading variant="h2" className="mb-2">
            {getSectionNumber(startNumber)}. {sectionTitle}
          </Heading>
          {sectionSubtitle && <Text variant="body">{sectionSubtitle}</Text>}
        </div>
      )}
      {items.map((item, index) => {
        const number = getNextNumber(startNumber, index);
        const isItemBold = item.isBold || isBold;
        const textVariant = isItemBold ? "bodyBold" : "body";
        const tone = isItemBold ? "dark" : "body";
        return (
          <div key={index} className="mb-6 pl-6">
            <div className="flex gap-3">
              <Text variant={textVariant} tone={tone} as="span" className="min-w-[2rem]">
                {number}
              </Text>
              <Text variant={textVariant} tone={tone} as="span" className="leading-relaxed">
                {item.text}
              </Text>
            </div>
            {item.subItems && item.subItems.length > 0 && (
              <ul className={`pl-8 mt-3 space-y-2 ${item.numberedSubItems ? "list-none" : "list-disc"}`}>
                {item.subItems.map((subItem, subIndex) => {
                  const subNumber = `${number}.${subIndex + 1}`;
                  return (
                    <li key={subIndex}>
                      {item.numberedSubItems ? (
                        <span className="flex gap-2">
                          <Text variant={textVariant} tone={tone} as="span" className="min-w-[3rem]">
                            {subNumber}
                          </Text>
                          <Text variant={textVariant} tone={tone} as="span">
                            {subItem}
                          </Text>
                        </span>
                      ) : (
                        <Text variant={textVariant} tone={tone} as="span" className="leading-relaxed">
                          {subItem}
                        </Text>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </>
  );
};
